import styled from '@emotion/styled';

import { colors } from '../../colors';
import type { StyledModalBackdropProps, StyledModalFooterProps, StyledModalProps } from './types';

export const StyledModalBackdrop = styled.div<StyledModalBackdropProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ $styleProps }) =>
    $styleProps?.backdropColor ?? `${colors.gray[800]}80`};
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ $styleProps }) => $styleProps?.zIndex ?? 9999999};
  padding: 20px;
  box-sizing: border-box;

  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledModal = styled.div<StyledModalProps>`
  background-color: ${({ $styleProps }) => $styleProps?.backgroundColor ?? colors.white};
  border-radius: ${({ $styleProps }) => $styleProps?.borderRadius ?? '10px'};
  max-width: ${({ $styleProps }) => $styleProps?.maxWidth ?? '600px'};
  min-width: ${({ $styleProps }) => $styleProps?.minWidth ?? '320px'};
  width: ${({ $styleProps }) => $styleProps?.width ?? '100%'};
  max-height: ${({ $styleProps }) => $styleProps?.maxHeight ?? '90vh'};
  min-height: ${({ $styleProps }) => $styleProps?.minHeight ?? 'auto'};
  height: ${({ $styleProps }) => $styleProps?.height ?? 'auto'};
  margin: ${({ $styleProps }) => $styleProps?.margin ?? 'auto'};
  border: ${({ $styleProps }) => $styleProps?.border ?? 'none'};
  display: flex;
  flex-direction: column;
  overflow: ${({ $styleProps }) => $styleProps?.overflow ?? 'hidden'};
  position: relative;

  animation: scaleIn 0.2s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    margin: 20px;
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 40px);
  }
`;

export const StyledModalHeader = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid ${colors.gray[100]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  h2,
  h3,
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: ${colors.gray[800]};
    line-height: 1.2;
  }
`;

export const StyledModalBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
  color: ${colors.gray[800]};
  line-height: 1.6;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export const StyledModalFooter = styled.div<StyledModalFooterProps>`
  padding: 20px 30px;
  border-top: 1px solid ${colors.gray[100]};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 12px;

  justify-content: ${({ $align }) => {
    switch ($align) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      case 'space-between':
        return 'space-between';
      default:
        return 'flex-end';
    }
  }};

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

export const StyledCloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  color: #6b7280;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;